import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeftIcon,
  MapPinIcon,
  CalendarIcon,
  CurrencyDollarIcon,
  UserIcon,
  PhoneIcon,
  TruckIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import { fetchAmbulanceBookingById } from '../../../services/MasterAdmin/ambulanceService';
import type { AmbulanceBookingDetail } from '../../../services/MasterAdmin/ambulanceService';

const formatDateTime = (value?: string | Date | null) => {
  if (!value) return '—';
  try {
    return format(new Date(value), 'dd MMM yyyy, HH:mm');
  } catch {
    return String(value);
  }
};

const formatInr = (n: number | null | undefined) => {
  if (n == null || Number.isNaN(Number(n))) return '—';
  return `₹${Number(n).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
};

const formatLabel = (value?: string | null) =>
  value
    ? String(value)
        .replace(/_/g, ' ')
        .toLowerCase()
        .replace(/\b\w/g, (c) => c.toUpperCase())
    : '—';

const statusBadgeClass = (bookingStatus?: string) => {
  const s = String(bookingStatus || '').toUpperCase();
  if (s === 'COMPLETED') return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
  if (s === 'CANCELLED' || s === 'REJECTED') return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
  if (s === 'ACCEPTED' || s === 'PAYMENT_DONE' || s === 'AMBULANCE_LEFT')
    return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
  return 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300';
};

function DetailSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-lg border border-gray-100 bg-gray-50/80 p-4 dark:border-gray-700 dark:bg-gray-900/30">
      <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-400">
        {title}
      </h3>
      {children}
    </section>
  );
}

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="grid grid-cols-1 gap-1 border-b border-gray-200/80 py-2 last:border-0 dark:border-gray-700/80 sm:grid-cols-3 sm:gap-4">
      <dt className="text-sm text-gray-500 dark:text-gray-400">{label}</dt>
      <dd className="text-sm font-medium text-gray-900 dark:text-white sm:col-span-2 break-words">{value}</dd>
    </div>
  );
}

const AmbulanceBookingDetailPage: React.FC = () => {
  const { vendorId, bookingId } = useParams<{ vendorId: string; bookingId: string }>();
  const navigate = useNavigate();
  const [booking, setBooking] = useState<AmbulanceBookingDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!vendorId || !bookingId) return;
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await fetchAmbulanceBookingById(vendorId, bookingId);
        setBooking(data);
      } catch {
        setError('Failed to load booking details.');
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, [vendorId, bookingId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-blue-600" />
      </div>
    );
  }

  if (error || !booking) {
    return (
      <div className="p-6 text-center">
        <p className="text-red-600 dark:text-red-400">{error || 'Booking not found'}</p>
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="mt-4 text-blue-600 hover:underline dark:text-blue-400"
        >
          Go back
        </button>
      </div>
    );
  }

  const ledgerHref = `/master-admin/operations/order-ledger/${encodeURIComponent(booking.bookingId)}?serviceType=AMBULANCE&returnTo=manual-refund-cases`;

  return (
    <div className="mx-auto w-full min-w-0 max-w-5xl space-y-6 p-3 sm:p-4 md:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="flex items-center text-sm text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
        >
          <ArrowLeftIcon className="mr-2 h-5 w-5" />
          Back
        </button>
        <div className="flex flex-wrap gap-2">
          {booking.userId && (
            <Link
              to={`/users/${booking.userId}`}
              className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-800 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-100 dark:hover:bg-gray-800"
            >
              User profile
            </Link>
          )}
          {vendorId && (
            <Link
              to={`/vendors/ambulance/${vendorId}`}
              className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-800 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-100 dark:hover:bg-gray-800"
            >
              Vendor dashboard
            </Link>
          )}
          <Link
            to={ledgerHref}
            className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-800 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-100 dark:hover:bg-gray-800"
          >
            Order ledger
          </Link>
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b px-6 py-4 dark:border-gray-700">
          <div>
            <p className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
              Ambulance booking
            </p>
            <h1 className="mt-1 font-mono text-sm text-gray-900 dark:text-white break-all">{booking.bookingId}</h1>
          </div>
          <span
            className={`rounded-full px-3 py-1 text-sm font-semibold ${statusBadgeClass(booking.bookingStatus)}`}
          >
            {formatLabel(booking.bookingStatus)}
          </span>
        </div>

        <div className="space-y-4 p-6">
          <DetailSection title="Overview">
            <dl>
              <DetailRow label="Booking status" value={formatLabel(booking.bookingStatus)} />
              <DetailRow label="Dispatch mode" value={formatLabel(booking.dispatchMode)} />
              <DetailRow
                label="Assigned agency"
                value={
                  booking.vendorId ? (
                    <span>
                      {booking.assignedAgencyName || '—'}{' '}
                      <span className="font-mono text-xs text-gray-500">({booking.vendorId})</span>
                    </span>
                  ) : (
                    'Not assigned (broadcast / pending accept)'
                  )
                }
              />
              <DetailRow label="Pickup type" value={formatLabel(booking.pickupTimeType)} />
              <DetailRow label="Scheduled pickup" value={formatDateTime(booking.scheduledPickupAt)} />
              <DetailRow label="Emergency" value={booking.isEmergency ? 'Yes' : 'No'} />
              <DetailRow label="Dead body transport" value={booking.isDeadBody ? 'Yes' : 'No'} />
              <DetailRow label="Return journey" value={booking.isReturnJourney ? 'Yes' : 'No'} />
            </dl>
          </DetailSection>

          <DetailSection title="Account user">
            <dl>
              <DetailRow
                label="User"
                value={
                  booking.userId ? (
                    <Link
                      to={`/users/${booking.userId}`}
                      className="text-primary-600 hover:underline dark:text-primary-400"
                    >
                      {booking.user?.name || booking.userId}
                    </Link>
                  ) : (
                    '—'
                  )
                }
              />
              <DetailRow label="Phone" value={booking.user?.phone_number || '—'} />
              <DetailRow label="Email" value={booking.user?.emailId || '—'} />
              <DetailRow label="User ID" value={<span className="font-mono text-xs">{booking.userId}</span>} />
            </dl>
          </DetailSection>

          <DetailSection title="Patient">
            <dl>
              <DetailRow label="Booking for" value={formatLabel(booking.bookingFor)} />
              <DetailRow label="Name" value={booking.patientName || '—'} />
              <DetailRow label="Phone" value={booking.patientPhone || '—'} />
              <DetailRow
                label="Age / gender"
                value={`${booking.patientAge != null ? booking.patientAge : '—'} / ${formatLabel(booking.patientGender)}`}
              />
            </dl>
          </DetailSection>

          <DetailSection title="Locations">
            <dl>
              <DetailRow label="Pickup address" value={booking.pickupAddress || '—'} />
              <DetailRow
                label="Pickup landmark"
                value={booking.pickupLandmark || '—'}
              />
              <DetailRow
                label="Pickup coordinates"
                value={
                  booking.pickupCoordinates
                    ? `${booking.pickupCoordinates.lat}, ${booking.pickupCoordinates.lng}`
                    : '—'
                }
              />
              <DetailRow label="Drop address" value={booking.dropAddress || '—'} />
              <DetailRow label="Drop landmark" value={booking.dropLandmark || '—'} />
              <DetailRow
                label="Drop coordinates"
                value={
                  booking.dropCoordinates
                    ? `${booking.dropCoordinates.lat}, ${booking.dropCoordinates.lng}`
                    : '—'
                }
              />
              <DetailRow
                label="Distance / ETA"
                value={`${booking.totalDistanceKm ?? '—'} km · ${booking.estimatedTravelTimeMinutes ?? '—'} min`}
              />
            </dl>
          </DetailSection>

          <DetailSection title="Vehicle & facilities">
            <dl>
              <DetailRow label="Ambulance type" value={formatLabel(booking.ambulanceType)} />
              <DetailRow
                label="Facilities"
                value={
                  Array.isArray(booking.facilities) && booking.facilities.length
                    ? booking.facilities.join(', ')
                    : '—'
                }
              />
            </dl>
          </DetailSection>

          <DetailSection title="Pricing & payment">
            <dl>
              <DetailRow label="Base fare" value={formatInr(booking.baseFare)} />
              <DetailRow label="Subtotal" value={formatInr(booking.subTotal)} />
              <DetailRow label="Total payable" value={formatInr(booking.totalPayableAmount)} />
              <DetailRow label="Payment method" value={formatLabel(booking.paymentMethod)} />
              <DetailRow label="Payment status" value={formatLabel(booking.paymentStatus)} />
              <DetailRow label="Payment completed" value={formatDateTime(booking.paymentCompletedAt)} />
              <DetailRow
                label="Payment ID"
                value={
                  booking.paymentId ? (
                    <span className="font-mono text-xs">{booking.paymentId}</span>
                  ) : (
                    '—'
                  )
                }
              />
            </dl>
          </DetailSection>

          <DetailSection title="Timeline">
            <dl>
              <DetailRow label="Created" value={formatDateTime(booking.createdAt)} />
              <DetailRow label="Accepted" value={formatDateTime(booking.acceptedAt)} />
              <DetailRow label="Call initiated" value={formatDateTime(booking.callInitiatedAt)} />
              <DetailRow label="Ambulance left" value={formatDateTime(booking.ambulanceLeftAt)} />
              <DetailRow label="Expected completion" value={formatDateTime(booking.expectedCompletionAt)} />
              <DetailRow label="Completed" value={formatDateTime(booking.completedAt)} />
              <DetailRow label="Last updated" value={formatDateTime(booking.updatedAt)} />
            </dl>
          </DetailSection>

          {(booking.cancelledAt ||
            booking.cancelledBy ||
            booking.cancellationReason ||
            booking.refundStatus !== 'NOT_REQUIRED') && (
            <DetailSection title="Cancellation & refund">
              <dl>
                <DetailRow label="Cancelled at" value={formatDateTime(booking.cancelledAt)} />
                <DetailRow label="Cancelled by" value={formatLabel(booking.cancelledBy)} />
                <DetailRow label="Reason" value={booking.cancellationReason || '—'} />
                <DetailRow label="Refund status" value={formatLabel(booking.refundStatus)} />
                <DetailRow label="Refund amount" value={formatInr(booking.refundAmount)} />
              </dl>
            </DetailSection>
          )}

          {booking.supportIssueNote && (
            <DetailSection title="Support issue">
              <p className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap">
                {booking.supportIssueNote}
              </p>
            </DetailSection>
          )}

          {(booking.dispatchMode === 'BROADCAST' ||
            (booking.invites && booking.invites.length > 0) ||
            (booking.staggeredBroadcastVendorIds && booking.staggeredBroadcastVendorIds.length > 0)) && (
            <DetailSection title="Broadcast & invites">
              {booking.staggeredBroadcastVendorIds && booking.staggeredBroadcastVendorIds.length > 0 && (
                <p className="mb-3 text-sm text-gray-600 dark:text-gray-400">
                  Staggered queue ({booking.staggeredBroadcastIssuedCount ?? 0} wave(s) issued):{' '}
                  <span className="font-mono text-xs">{booking.staggeredBroadcastVendorIds.join(', ')}</span>
                </p>
              )}
              {booking.invites && booking.invites.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="border-b text-left text-xs uppercase text-gray-500 dark:border-gray-600">
                        <th className="py-2 pr-4">Agency</th>
                        <th className="py-2 pr-4">Status</th>
                        <th className="py-2 pr-4">Mode</th>
                        <th className="py-2">Invited at</th>
                      </tr>
                    </thead>
                    <tbody>
                      {booking.invites.map((inv) => (
                        <tr key={inv.inviteId} className="border-b border-gray-200/80 dark:border-gray-700/80">
                          <td className="py-2 pr-4">
                            <Link
                              to={`/vendors/ambulance/${inv.vendorId}`}
                              className="text-primary-600 hover:underline dark:text-primary-400"
                            >
                              {inv.agencyName || inv.vendorId}
                            </Link>
                          </td>
                          <td className="py-2 pr-4">{formatLabel(inv.status)}</td>
                          <td className="py-2 pr-4">{formatLabel(inv.mode)}</td>
                          <td className="py-2">{formatDateTime(inv.createdAt)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-sm text-gray-500">No invite rows recorded.</p>
              )}
            </DetailSection>
          )}
        </div>
      </div>
    </div>
  );
};

export default AmbulanceBookingDetailPage;
